import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import api from '../lib/api';
import { toast } from 'sonner';
import { 
  Zap, 
  TrendingUp, 
  TrendingDown, 
  Gift, 
  ShoppingCart, 
  RefreshCw,
  ArrowRight,
  Clock,
  CheckCircle2,
  CreditCard,
  Plus
} from 'lucide-react';

export default function Credits() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [credits, setCredits] = useState(null);
  const [creditLogs, setCreditLogs] = useState([]);
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [selectedCreditAmount, setSelectedCreditAmount] = useState(null);
  const [customCreditAmount, setCustomCreditAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [creditPackages, setCreditPackages] = useState([]);

  useEffect(() => {
    loadCreditsData();
    loadCreditPackages();
  }, []);

  const loadCreditsData = async () => {
    try {
      const [creditsRes, logsRes] = await Promise.all([
        api.get('/credits'),
        api.get('/credits/logs')
      ]);

      setCredits(creditsRes.data);
      setCreditLogs(logsRes.data || []);
    } catch (error) {
      toast.error('Kredi bilgileri yüklenirken hata oluştu');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadCreditPackages = async () => {
    try {
      const response = await api.get('/credit-packages');
      setCreditPackages(response.data || []);
    } catch (error) {
      console.error('Credit packages load error:', error);
      // Fallback to default packages if API fails
      setCreditPackages([
        { id: '1', credits: 100, price: 50, popular: false },
        { id: '2', credits: 500, price: 200, popular: true },
        { id: '3', credits: 1000, price: 350, popular: false },
        { id: '4', credits: 2000, price: 600, popular: false }
      ]);
    }
  };

  const handleSelectPackage = () => {
    const amount = selectedCreditAmount || parseInt(customCreditAmount);
    
    if (!amount || amount < 10) {
      toast.error('Lütfen en az 10 kredi seçin');
      return;
    }

    // Redirect to checkout page with credits parameter
    navigate(`/checkout?credits=${amount}`);
    setCreditDialogOpen(false);
  };

  const getLogIcon = (type) => {
    switch (type) {
      case 'welcome_bonus':
      case 'bonus':
        return <Gift className="h-5 w-5 text-green-600" />;
      case 'purchase':
        return <ShoppingCart className="h-5 w-5 text-blue-600" />;
      case 'new_question_trendyol':
      case 'new_question_hepsiburada':
        return <TrendingDown className="h-5 w-5 text-orange-600" />;
      case 'regenerate_answer':
      case 'retry_ai_answer':
        return <RefreshCw className="h-5 w-5 text-purple-600" />;
      default:
        return <Zap className="h-5 w-5 text-slate-600" />;
    }
  };

  const getLogLabel = (type) => {
    switch (type) {
      case 'welcome_bonus':
        return 'Hoş Geldin Hediyesi';
      case 'bonus':
        return 'Bonus Kredi';
      case 'purchase':
        return 'Kredi Satın Alımı';
      case 'new_question_trendyol':
        return 'Trendyol Sorusu';
      case 'new_question_hepsiburada':
        return 'Hepsiburada Sorusu';
      case 'regenerate_answer':
        return 'Cevap Yenileme';
      case 'retry_ai_answer':
        return 'AI Tekrar Deneme';
      default:
        return 'Kredi İşlemi';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-6xl mx-auto px-4 py-8">
          <Skeleton className="h-48 mb-6" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Kredi Yönetimi</h1>
          <p className="text-slate-600">Kredi bakiyeniz ve işlem geçmişiniz</p>
        </div>

        {/* Credit Balance Cards */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <Card className="border-2 border-[#FF6000]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-600">Toplam Kredi</span>
                <Zap className="h-5 w-5 text-[#FF6000]" />
              </div>
              <div className="text-3xl font-bold text-[#FF6000]">
                {credits?.total_credits || 0}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {credits?.questions_remaining || 0} soru hakkı
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-600">Normal Kredi</span>
                <ShoppingCart className="h-5 w-5 text-blue-600" />
              </div>
              <div className="text-3xl font-bold">
                {credits?.credits || 0}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-600">Bonus Kredi</span>
                <Gift className="h-5 w-5 text-green-600" />
              </div>
              <div className="text-3xl font-bold text-green-600">
                {credits?.bonus_credits || 0}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-600">Kullanılan</span>
                <TrendingDown className="h-5 w-5 text-slate-600" />
              </div>
              <div className="text-3xl font-bold">
                {credits?.total_credits_used || 0}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Big Buy Credits Button */}
        <div className="mb-6">
          <Card className="border-2 border-[#FF6000] bg-gradient-to-r from-orange-50 to-amber-50">
            <CardContent className="p-8">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-[#FF6000] mb-2">
                    💳 Kredi Satın Al
                  </h2>
                  <p className="text-slate-700">
                    Hızlı ve güvenli ödeme ile anında kredi yükleyin
                  </p>
                </div>
                <Button 
                  onClick={() => setCreditDialogOpen(true)}
                  className="bg-[#FF6000] hover:bg-orange-600 text-white px-8 py-6 text-lg"
                  size="lg"
                >
                  <ShoppingCart className="h-6 w-6 mr-3" />
                  Kredi Satın Al
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Low Credit Warning */}
        {credits && credits.total_credits < 50 && (
          <Card className="mb-6 border-2 border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <TrendingDown className="h-6 w-6 text-orange-600" />
                  <div>
                    <h3 className="font-semibold text-orange-900">Krediniz Azalıyor!</h3>
                    <p className="text-sm text-orange-700">
                      Hizmetlerinizin kesintisiz devam etmesi için kredi satın alın.
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={() => setCreditDialogOpen(true)}
                  className="bg-[#FF6000] hover:bg-orange-600"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Kredi Satın Al
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Transaction History */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>İşlem Geçmişi</span>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setCreditDialogOpen(true)}
              >
                <ShoppingCart className="h-4 w-4 mr-2" />
                Kredi Satın Al
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {creditLogs.length === 0 ? (
              <div className="text-center py-12">
                <Clock className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-600 mb-4">Henüz işlem geçmişi yok</p>
                <p className="text-sm text-slate-500 mb-6">
                  Kredi satın aldığınızda veya kullandığınızda burada görünecek
                </p>
                <Button 
                  onClick={() => setCreditDialogOpen(true)}
                  className="bg-[#FF6000] hover:bg-orange-600"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  İlk Kredimi Satın Al
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {creditLogs.map((log, index) => (
                  <div 
                    key={log._id || index}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      {getLogIcon(log.type)}
                      <div>
                        <div className="font-medium">{getLogLabel(log.type)}</div>
                        {log.note && (
                          <div className="text-sm text-slate-500">{log.note}</div>
                        )}
                        <div className="text-xs text-slate-400 mt-1">
                          {new Date(log.created_at).toLocaleString('tr-TR')}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`text-lg font-bold ${
                        log.amount > 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {log.amount > 0 ? '+' : ''}{log.amount}
                      </div>
                      <Badge variant={log.type === 'bonus' ? 'secondary' : 'outline'} className="text-xs">
                        {log.type === 'bonus' || log.type === 'welcome_bonus' ? 'Bonus' : 'Normal'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Credit Purchase Dialog */}
      <Dialog open={creditDialogOpen} onOpenChange={setCreditDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Kredi Satın Al</DialogTitle>
            <DialogDescription>
              İhtiyacınız kadar kredi seçin ve güvenli ödeme sayfasına geçin.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-6 space-y-6">
            {/* Credit Packages */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Hazır Paketler</h3>
              <div className="grid grid-cols-2 gap-3">
                {creditPackages.map((pkg) => (
                  <button
                    key={pkg.id}
                    onClick={() => {
                      setSelectedCreditAmount(pkg.credits);
                      setCustomCreditAmount('');
                    }}
                    className={`relative p-4 border-2 rounded-lg transition-all ${
                      selectedCreditAmount === pkg.credits
                        ? 'border-[#FF6000] bg-orange-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {pkg.popular && (
                      <span className="absolute -top-2 right-2 px-2 py-0.5 text-xs font-semibold bg-[#FF6000] text-white rounded-full">
                        Popüler
                      </span>
                    )}
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-900">{pkg.credits}</p>
                      <p className="text-xs text-gray-500 mb-2">Kredi</p>
                      <p className="text-lg font-semibold text-[#FF6000]">{pkg.price} ₺</p>
                      <p className="text-xs text-gray-400">{(pkg.price / pkg.credits).toFixed(2)} ₺/kredi</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Amount */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Özel Miktar</h3>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <Input
                    type="number"
                    placeholder="Kredi miktarı (min: 10)"
                    value={customCreditAmount}
                    onChange={(e) => {
                      setCustomCreditAmount(e.target.value);
                      setSelectedCreditAmount(null);
                    }}
                    min="10"
                    className="w-full"
                  />
                </div>
                {customCreditAmount && parseInt(customCreditAmount) >= 10 && (
                  <div className="text-right">
                    <p className="text-sm font-semibold text-gray-900">
                      {Math.ceil(parseInt(customCreditAmount) * 0.5)} ₺
                    </p>
                    <p className="text-xs text-gray-500">0.50 ₺/kredi</p>
                  </div>
                )}
              </div>
            </div>

            {/* Summary */}
            {(selectedCreditAmount || (customCreditAmount && parseInt(customCreditAmount) >= 10)) && (
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">Kredi Miktarı:</span>
                  <span className="font-semibold text-gray-900">
                    {selectedCreditAmount || parseInt(customCreditAmount)} kredi
                  </span>
                </div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm text-gray-600">Toplam Tutar:</span>
                  <span className="text-xl font-bold text-[#FF6000]">
                    {selectedCreditAmount 
                      ? creditPackages.find(p => p.credits === selectedCreditAmount)?.price 
                      : Math.ceil(parseInt(customCreditAmount) * 0.5)
                    } ₺
                  </span>
                </div>
                <div className="border-t border-gray-200 pt-3">
                  <p className="text-xs text-gray-500">
                    💳 Ödeme sayfasında kredi/banka kartınız ile güvenli ödeme yapacaksınız.
                  </p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setCreditDialogOpen(false);
                  setSelectedCreditAmount(null);
                  setCustomCreditAmount('');
                }}
                className="flex-1"
                disabled={submitting}
              >
                İptal
              </Button>
              <Button
                onClick={handleSelectPackage}
                disabled={submitting || (!selectedCreditAmount && !customCreditAmount)}
                className="flex-1 bg-[#FF6000] hover:bg-orange-600 text-white"
              >
                <ArrowRight className="w-4 h-4 mr-2" />
                Devam Et
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
